import ClassNames from "classnames";
import { FunctionComponent, useState } from "react";
import { SlideImage } from "yet-another-react-lightbox";
import globalTranslations from "../i18n/index.js";
import { selectActiveSite } from "../selectors/sites.js";
import { useAppSelector } from "../store/hooks.js";
import styles from "../stylesheets/components/image-gallery-layout-1.module.css";
import moduleBaseStyles from "../stylesheets/components/module-base.module.css";
import {
  ColorScheme,
  ContainerQueries,
  ImageModuleSettings,
  Language,
  TitleAndSubtitleTextAlign,
  TranslatedModule,
} from "../types/index.js";
import { css } from "../utils/emotion.js";
import {
  getGalleryMediaAspectRatio,
  getPlaceholderImage,
  getTranslations,
} from "../utils/utils.js";
import Icon from "./Icon.js";
import ModuleHeadings from "./ModuleHeadings.js";
import ImageModule from "./Modules/ImageModule.js";

interface Props {
  title: string;
  subtitle: string;
  textAlign: TitleAndSubtitleTextAlign;
  scheme: ColorScheme;
  isFirstOnPage: boolean;
  imageModules: TranslatedModule<ImageModuleSettings>[];
  queries: ContainerQueries;
  slides: SlideImage[];
  isPreview: boolean;
  languageId: Language;
  pageId: string;
  moduleId: string;
  onClickImage: (index: number) => void;
}

const ImageGalleryLayout1: FunctionComponent<Props> = ({
  title,
  subtitle,
  textAlign,
  isFirstOnPage,
  scheme,
  imageModules,
  queries,
  slides,
  isPreview,
  languageId,
  pageId,
  moduleId,
  onClickImage,
}) => {
  const i18n = getTranslations(languageId, globalTranslations);
  const site = useAppSelector(selectActiveSite);

  const [maxImagesToShow, setMaxImagesToShow] = useState(5);

  const images = imageModules.length
    ? imageModules
    : new Array(5)
        .fill(true)
        .map((_, index) =>
          getPlaceholderImage({
            siteId: site.id,
            pageId,
            languageId,
            parentId: moduleId,
            index,
          }),
        );

  const showMoreButton = maxImagesToShow < images.length;
  const shownImages = images.slice(0, maxImagesToShow);

  return (
    <div
      className={ClassNames(
        moduleBaseStyles.Module__Wrapper,
        moduleBaseStyles["Module__Wrapper--small-padding"],
      )}
    >
      <ModuleHeadings
        textAlign={textAlign}
        scheme={scheme}
        isFirstOnPage={isFirstOnPage}
        title={title}
        subtitle={subtitle}
      />

      <div className={styles.ImageGalleryLayout1__Wrapper}>
        {shownImages.map((imageModule, index) => {
          const item = slides[index];
          return (
            <a
              href={isPreview || !item ? undefined : item.src}
              className={styles.ImageGalleryLayout1__Item}
              key={imageModule.id}
              onClick={(e) => {
                e.preventDefault();
                onClickImage(index);
              }}
            >
              <ImageModule
                aspectRatio={getAspectRatio(index, queries)}
                sizes="50vw"
                lazyLoad={true}
                width={1920}
                translatedModule={imageModule}
                isPreview={isPreview}
                isSlider={false}
                pageId={pageId}
                scheme={scheme}
                showOverlay={false}
              />
            </a>
          );
        })}
      </div>
      {showMoreButton && (
        <div className={styles.ImageGalleryLayout1__ButtonWrap}>
          <button
            className={ClassNames(
              "Btn--bare",
              css({ color: scheme.primary.background }),
            )}
            onClick={() => setMaxImagesToShow(maxImagesToShow + 8)}
            title={i18n.more}
          >
            <Icon
              className={styles.ImageGalleryLayout1__Icon}
              glyph="arrow-down"
            />
          </button>
        </div>
      )}
    </div>
  );
};

const getAspectRatio = (index: number, queries: ContainerQueries): number => {
  const aspectRatios = [0.6667, 1.3333] as const;

  if (queries["Query--medium"])
    return getGalleryMediaAspectRatio(index, "layout-1");

  return aspectRatios[index] ?? getGalleryMediaAspectRatio(index, "layout-1");
};

export default ImageGalleryLayout1;
